import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Activity, BarChart3, Zap, Users, TrendingUp as TrendingUpIcon, PieChart, Clock, Flame } from 'lucide-react';
import { apiService } from '../../services/api';
import { formatWssReason } from '../../utils/wssReasons';
import { useAuth } from '../../contexts/AuthContext';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import { trackUpgradeClick } from '../../utils/analytics';

const Landing: React.FC = () => {
  const { isAuthenticated, isPro, loading: authLoading } = useAuth();
  const [topTicker, setTopTicker] = useState<string | null>(null);
  const [mentionsData, setMentionsData] = useState<any[]>([]);
  const [topTickers, setTopTickers] = useState<any[]>([]);
  const [overallStats, setOverallStats] = useState<any>(null);
  const [sentimentDistribution, setSentimentDistribution] = useState<any>(null);
  const [wssAnalysis, setWssAnalysis] = useState<any>(null);

  const renderWssSignalCard = (wa: any) => {
    const dir = (wa.direction || 'neutral').toLowerCase();
    const isBull = dir === 'bullish' || dir === 'up';
    const isBear = dir === 'bearish' || dir === 'down';
    const tone = isBull ? 'bull' : isBear ? 'bear' : 'neutral';
    const signalLabel = isBull ? 'BULLISH' : isBear ? 'BEARISH' : 'NEUTRAL';
    const volumeTrendLabel = formatWssReason(wa.wssReason);
    const confidencePct = wa.confidence != null && wa.confidence > 0 ? Math.round(wa.confidence * 100) : 0;
    return (
      <div className={`landing-wss-card landing-wss-card--${tone}`}>
        <div className="landing-wss-card__header">
          <div className="landing-wss-card__title">Sentiment</div>
          <div className="landing-wss-card__row">
            <div className={`landing-wss-card__dot landing-wss-card__dot--${tone}`} />
            <span className={`landing-wss-card__label landing-wss-card__label--${tone}`}>
              {signalLabel}
            </span>
            {confidencePct > 0 && (
              <span className="landing-wss-card__confidence">({confidencePct}% confidence)</span>
            )}
          </div>
        </div>
        {volumeTrendLabel && (
          <div className="landing-wss-card__volume">
            <span className="landing-wss-card__volume-label">Volume trend:</span>
            {volumeTrendLabel}
          </div>
        )}
      </div>
    );
  };

  // Состояние авторизации и проверка токена — только в AuthContext (checkAuth), лишний /me не вызываем

  useEffect(() => {
    if (authLoading) return;
    const fetchData = async () => {
      // isPro из AuthContext — запрос только после загрузки auth, чтобы PRO не получал free/analytics
      try {
        // Один вызов API для получения топ тикеров за 12 часов
        const response = await apiService.getTopTickers(12, 10, 'mentions', 'desc', isPro);
        if (response && response.tickers && response.tickers.length > 0) {
          // Преобразуем формат
          const tickers = response.tickers.map(t => ({
            ticker: t.ticker,
            mentions: t.mentions,
            avg_sentiment: t.avgSentiment,
          }));
          
          // Устанавливаем топ тикер для графика
          setTopTicker(tickers[0].ticker);
          // Устанавливаем список тикеров для отображения
          setTopTickers(tickers);
          
          // Получаем данные для графика топ тикера (12 часов для FREE)
          const tickerData = await apiService.getAnalytics(tickers[0].ticker, 12, 60, isPro);
          if (tickerData) {
            // Сохраняем общую статистику
            if (tickerData.overallStats) {
              setOverallStats(tickerData.overallStats);
            }
            
            // Сохраняем распределение сентимента
            if (tickerData.sentimentDistribution) {
              setSentimentDistribution(tickerData.sentimentDistribution);
            }
            
            // Сохраняем WSS анализ
            if (tickerData.wssAnalysis) {
              setWssAnalysis(tickerData.wssAnalysis);
            }
            
            if (tickerData.timeSeries && tickerData.timeSeries.length > 0) {
              // Просто преобразуем данные из API в формат для графика
              // API использует timestampUtc, а не timestamp
              const processedData = tickerData.timeSeries
                .map((item: any) => {
                  try {
                    // Пробуем timestampUtc или timestamp
                    const timestamp = item.timestampUtc || item.timestamp;
                    const date = new Date(timestamp);
                    if (isNaN(date.getTime())) {
                      return null;
                    }
                    
                    // Форматируем время для отображения
                    const timeString = date.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: false 
                    });
                    
                    return {
                      date: timeString,
                      mentions: item.mentions || 0,
                      sentiment: item.sentiment || item.avgSentiment || 0,
                      timestamp: timestamp, // Сохраняем для сортировки
                    };
                  } catch {
                    return null;
                  }
                })
                .filter((item: any) => item !== null) // Убираем невалидные записи
                .sort((a: any, b: any) => {
                  // Сортируем по времени
                  const timeA = new Date(a.timestamp).getTime();
                  const timeB = new Date(b.timestamp).getTime();
                  return timeA - timeB;
                })
                .map((item: any) => ({
                  date: item.date,
                  mentions: item.mentions,
                  sentiment: item.sentiment,
                }));
              // Убираем хвостовые точки с нулевыми mentions (последний неполный интервал с API)
              let trimmed = processedData;
              while (trimmed.length > 1 && (trimmed[trimmed.length - 1].mentions ?? 0) === 0) trimmed = trimmed.slice(0, -1);
              setMentionsData(trimmed);
            } else {
              // Если данных нет, создаем пустые точки
              const now = new Date();
              const emptyData = [];
              for (let i = 0; i < 12; i++) {
                const targetTime = new Date(now.getTime() - (11 - i) * 60 * 60 * 1000);
                const timeString = targetTime.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: false 
                });
                emptyData.push({
                  date: timeString,
                  mentions: 0,
                  sentiment: 0,
                });
              }
              setMentionsData(emptyData);
            }
          }
        }
      } catch (e) {
        // ignore
      }
    };

    fetchData();
  }, [authLoading, isPro]);

  return (
    <>
      {/* Glow effects */}
      <div className="glow-orange glow-orange--landing-top-left"></div>
      <div className="glow-orange glow-orange--landing-bottom-right"></div>

      {/* Main Content */}
      <div className="content">
        <div className="container">
          {/* FREE plan notice: план и задержка данных */}
          {!isPro && (
            <div role="status" className="free-plan-notice">
              <span className="free-plan-notice__badge">FREE</span>
              <Clock className="free-plan-notice__icon" aria-hidden />
              <span>All data is delayed by 6 hours.</span>
              <Link
                to={isAuthenticated ? '/billing' : '/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
                className="free-plan-notice__link"
                onClick={() => trackUpgradeClick('landing_free_notice')}
              >
                Upgrade to Premium for real-time
              </Link>
            </div>
          )}

          {/* Hero Section */}
          <section className="hero">
            <div className="hero-grid">
              {/* Left: Hero Text + Trending Stocks */}
              <div>
                <h1 className="hero-title">
                  Spot the <span className="text-orange">Hype</span><br />Ride the <span className="text-green">Wave</span>
                </h1>
                <p className="hero-subtitle hero-subtitle--landing">
                  Catch the momentum before it hits the mainstream. Track real-time sentiment from the most active trading communities and get ahead of the curve with data-driven insights that help you spot emerging trends before they go viral.
                </p>
                
                {/* Trending Stocks */}
                {topTickers.length > 0 && (
                  <div className="landing-trending-wrap">
                    <h2 className="section-title landing-trending-title">Trending Stocks</h2>
                    <div className="trending-grid">
                      {topTickers.slice(0, 4).map((ticker, idx) => (
                        <Link key={ticker.ticker} to={`/ticker/${ticker.ticker}`} className="stock-card">
                          <div className="stock-info">
                            <div className="stock-details">
                              <span className="stock-name">{ticker.ticker}</span>
                              <p className="stock-mentions">{ticker.mentions} mentions</p>
                            </div>
                            <div className="stock-chart-section">
                              <div className="stock-sentiment-indicator">
                                {ticker.avg_sentiment && ticker.avg_sentiment > 0.1 ? (
                                  <TrendingUp className="w-8 h-8 text-[#41D27D] landing-sentiment-icon--bull" />
                                ) : ticker.avg_sentiment && ticker.avg_sentiment < -0.1 ? (
                                  <TrendingDown className="w-8 h-8 text-[#FF5252] landing-sentiment-icon--bear" />
                                ) : (
                                  <Minus className="w-8 h-8 text-[#b0b0b0] landing-sentiment-icon--neutral" />
                                )}
                                {/* Индикатор силы sentiment */}
                                {ticker.avg_sentiment && (
                                  <div className="sentiment-strength-bar landing-strength-bar">
                                    <div style={{
                                      width: `${Math.min(Math.abs(ticker.avg_sentiment) * 100, 100)}%`,
                                      height: '100%',
                                      background: ticker.avg_sentiment > 0 
                                        ? 'linear-gradient(90deg, #41D27D, #10b981)' 
                                        : ticker.avg_sentiment < 0 
                                        ? 'linear-gradient(90deg, #FF5252, #dc2626)' 
                                        : 'linear-gradient(90deg, #b0b0b0, #888)',
                                      borderRadius: '2px'
                                    }} />
                                  </div>
                                )}
                              </div>
                              <span className={`stock-price ${ticker.avg_sentiment && ticker.avg_sentiment > 0 ? 'text-green' : ticker.avg_sentiment && ticker.avg_sentiment < 0 ? 'text-red' : 'text-dim'}`}>
                                {ticker.avg_sentiment ? (ticker.avg_sentiment > 0 ? '+' : '') + ticker.avg_sentiment.toFixed(2) + '%' : 'N/A'}
                              </span>
                            </div>
                          </div>
                          <div className={`stock-status ${ticker.avg_sentiment && ticker.avg_sentiment > 0 ? 'status-bullish' : ticker.avg_sentiment && ticker.avg_sentiment < 0 ? 'status-bearish' : 'status-neutral'}`}>
                            {ticker.avg_sentiment && ticker.avg_sentiment > 0 ? 'Bullish' : ticker.avg_sentiment && ticker.avg_sentiment < 0 ? 'Bearish' : 'Neutral'}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right: Market Pulse */}
              <div className="card-glass landing-dashboard-preview-card">
                <div className="landing-dashboard-preview-header">
                  <BarChart3 className="w-6 h-6 landing-dashboard-preview-icon" />
                  <h3 className="landing-dashboard-preview-title">Market Pulse</h3>
                </div>
              
              {topTicker && (
                <>
                  {/* Top Ticker Header */}
                  <div className="landing-top-ticker-box">
                    <div className="landing-top-ticker-row">
                      <div className="landing-top-ticker-meta">
                        <span className="landing-top-ticker-symbol">
                          ${topTicker}
                        </span>
                        {topTickers[0]?.avg_sentiment && (
                          <span
                            className={`landing-top-ticker-badge ${
                              topTickers[0].avg_sentiment > 0.1
                                ? 'landing-top-ticker-badge--bull'
                                : topTickers[0].avg_sentiment < -0.1
                                  ? 'landing-top-ticker-badge--bear'
                                  : 'landing-top-ticker-badge--neutral'
                            }`}
                          >
                            {topTickers[0].avg_sentiment > 0.1 ? 'Bullish' : topTickers[0].avg_sentiment < -0.1 ? 'Bearish' : 'Neutral'}
                          </span>
                        )}
                      </div>
                      {mentionsData.length > 0 && (
                        <div className="landing-top-ticker-mentions">
                          <Activity className="w-3.5 h-3.5 landing-top-ticker-mentions-icon" />
                          <div className="landing-top-ticker-mentions-meta">
                            <span className="landing-top-ticker-mentions-value">
                              {overallStats?.totalMentions || 0}
                            </span>
                            <span className="landing-top-ticker-mentions-label">
                              Last 12h{!isPro && ' (6h delay)'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Enhanced Stats Grid */}
                    {overallStats && (
                      <div className="landing-stats-grid">
                        <div className="landing-stats-card landing-stats-card--mentions">
                          <div className="landing-stats-card-label-row">
                            <Activity className="w-3.5 h-3.5 landing-stats-card-icon--gold" />
                            <div className="landing-stats-card-label">Total Mentions (12h){!isPro && ' · 6h delay'}</div>
                          </div>
                          <div className="landing-stats-card-value landing-stats-card-value--gold">
                            {overallStats.totalMentions || 0}
                          </div>
                          <div className="landing-stats-card-meta">
                            Avg: {overallStats.totalMentions != null ? Math.round(overallStats.totalMentions / 12) : '—'}/hr
                          </div>
                        </div>
                        
                        <div
                          className={`landing-stats-card ${
                            overallStats.avgSentimentOverall > 0.1
                              ? 'landing-stats-card--sentiment-bull'
                              : overallStats.avgSentimentOverall < -0.1
                                ? 'landing-stats-card--sentiment-bear'
                                : 'landing-stats-card--sentiment-neutral'
                          }`}
                        >
                          <div className="landing-stats-card-label-row">
                            <TrendingUpIcon
                              className={`w-3.5 h-3.5 ${
                                overallStats.avgSentimentOverall > 0.1
                                  ? 'landing-stats-card-icon--bull'
                                  : overallStats.avgSentimentOverall < -0.1
                                    ? 'landing-stats-card-icon--bear'
                                    : 'landing-stats-card-icon--neutral'
                              }`}
                            />
                            <div className="landing-stats-card-label">Avg Sentiment</div>
                          </div>
                          <div
                            className={`landing-stats-card-value ${
                              overallStats.avgSentimentOverall > 0.1
                                ? 'landing-stats-card-value--bull'
                                : overallStats.avgSentimentOverall < -0.1
                                  ? 'landing-stats-card-value--bear'
                                  : 'landing-stats-card-value--neutral'
                            }`}
                          >
                            {overallStats.avgSentimentOverall ? (overallStats.avgSentimentOverall > 0 ? '+' : '') + (overallStats.avgSentimentOverall * 100).toFixed(1) + '%' : 'N/A'}
                          </div>
                          {overallStats.sentimentVolatility && (
                            <div className="landing-stats-card-meta">
                              Volatility: {(overallStats.sentimentVolatility * 100).toFixed(1)}%
                            </div>
                          )}
                        </div>
                        
                        {overallStats.maxMentions && (
                          <div className="landing-stats-card landing-stats-card--default">
                            <div className="landing-stats-card-label">Peak Hour</div>
                            <div className="landing-stats-card-value landing-stats-card-value--gold-sm">
                              {overallStats.maxMentions}
                            </div>
                            <div className="landing-stats-card-meta">
                              Min: {overallStats.minMentions || 0}
                            </div>
                          </div>
                        )}
                        
                        {overallStats.totalUniqueUsersAllTime && (
                          <div className="landing-stats-card landing-stats-card--default">
                            <div className="landing-stats-card-label-row">
                              <Users className="w-3.5 h-3.5 landing-stats-card-icon--gold" />
                              <div className="landing-stats-card-label">Unique Users (12h){!isPro && ' · 6h delay'}</div>
                            </div>
                            <div className="landing-stats-card-value landing-stats-card-value--gold-sm">
                              {overallStats.totalUniqueUsersAllTime}
                            </div>
                            <div className="landing-stats-card-meta">
                              Avg: {overallStats.totalUniqueUsersAllTime != null ? Math.round(overallStats.totalUniqueUsersAllTime / 12) : '—'}/hr
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Sentiment Distribution Pie Chart */}
                    {sentimentDistribution && (
                      <div className="landing-sentiment-distribution">
                        <div className="landing-sentiment-distribution-header">
                          <PieChart className="w-4 h-4 landing-sentiment-distribution-icon" />
                          <span className="landing-sentiment-distribution-title">
                            Sentiment Distribution
                          </span>
                        </div>
                        <ResponsiveContainer width="100%" height={140}>
                          <RechartsPieChart>
                            <Pie
                              data={[
                                { name: 'Positive', value: sentimentDistribution.positiveMentions || 0, color: '#41D27D' },
                                { name: 'Neutral', value: sentimentDistribution.neutralMentions || 0, color: '#b0b0b0' },
                                { name: 'Negative', value: sentimentDistribution.negativeMentions || 0, color: '#FF5252' },
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={false}
                              outerRadius={50}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {[
                                { name: 'Positive', value: sentimentDistribution.positiveMentions || 0, color: '#41D27D' },
                                { name: 'Neutral', value: sentimentDistribution.neutralMentions || 0, color: '#b0b0b0' },
                                { name: 'Negative', value: sentimentDistribution.negativeMentions || 0, color: '#FF5252' },
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              wrapperStyle={{ zIndex: 1000 }}
                              contentStyle={{ 
                                backgroundColor: 'rgba(10, 10, 15, 0.98)', 
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                color: '#fff',
                                padding: '8px 12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                                fontSize: '12px'
                              }}
                              labelStyle={{ 
                                color: '#D4AF37', 
                                fontWeight: '600', 
                                marginBottom: '4px',
                                fontSize: '13px'
                              }}
                              itemStyle={{ color: '#fff', fontSize: '12px' }}
                              formatter={(value: any, name: any) => {
                                const total = (sentimentDistribution.positiveMentions || 0) + 
                                             (sentimentDistribution.neutralMentions || 0) + 
                                             (sentimentDistribution.negativeMentions || 0);
                                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                return [`${value} mentions (${percent}%)`, name];
                              }}
                            />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                        <div className="landing-sentiment-distribution-legend">
                          <div className="landing-sentiment-legend-item">
                            <div className="landing-sentiment-legend-dot landing-sentiment-legend-dot--bull"></div>
                            <span>Positive: {sentimentDistribution.positiveMentions || 0}</span>
                          </div>
                          <div className="landing-sentiment-legend-item">
                            <div className="landing-sentiment-legend-dot landing-sentiment-legend-dot--neutral"></div>
                            <span>Neutral: {sentimentDistribution.neutralMentions || 0}</span>
                          </div>
                          <div className="landing-sentiment-legend-item">
                            <div className="landing-sentiment-legend-dot landing-sentiment-legend-dot--bear"></div>
                            <span>Negative: {sentimentDistribution.negativeMentions || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* WSS Analysis — показываем только при наличии осмысленных данных */}
                    {wssAnalysis && (wssAnalysis.wssReason === 'insufficient_data' || !wssAnalysis.wssReason ? (
                      <div className="landing-wss-empty-state">
                        WSS: Not enough data yet
                      </div>
                    ) : renderWssSignalCard(wssAnalysis))}
                  </div>
                  
                  {/* Mentions Chart */}
                  {mentionsData.length > 0 && (
                    <div className="landing-mentions-chart-wrap">
                      <div className="landing-mentions-chart-header">
                        <Zap className="w-3.5 h-3.5 landing-mentions-chart-icon" />
                        <span className="landing-mentions-chart-title">
                          Mentions (12h){!isPro && ' · 6h delay'}
                        </span>
                      </div>
                      <ResponsiveContainer width="100%" height={140}>
                        <AreaChart data={mentionsData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorMentions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#41D27D" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="#41D27D" stopOpacity={0.05}/>
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="date" 
                            stroke="rgba(255,255,255,0.4)"
                            fontSize={10}
                            tick={{ fill: 'rgba(255,255,255,0.6)' }}
                            interval={mentionsData.length > 6 ? Math.floor(mentionsData.length / 6) : 0}
                            angle={-45}
                            textAnchor="end"
                            height={50}
                          />
                          <YAxis 
                            stroke="rgba(255,255,255,0.4)"
                            fontSize={10}
                            tick={{ fill: 'rgba(255,255,255,0.6)' }}
                            width={40}
                            domain={[0, 'dataMax + 5']}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(10, 10, 15, 0.95)', 
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px',
                              color: '#fff',
                              padding: '8px 12px'
                            }}
                            labelStyle={{ color: '#D4AF37', fontWeight: '600', marginBottom: '4px' }}
                            formatter={(value: any) => [`${value} mentions`, 'Mentions']}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="mentions" 
                            stroke="#41D27D" 
                            strokeWidth={2}
                            fill="url(#colorMentions)"
                            dot={mentionsData.length <= 12 ? { fill: '#41D27D', strokeWidth: 2, r: 3 } : false}
                            activeDot={{ r: 5, stroke: '#41D27D', strokeWidth: 2 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  
                  {/* CTA Link */}
                  <div className="landing-cta-wrap">
                    <Link 
                      to={isAuthenticated ? `/ticker/${topTicker}` : '/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
                      onClick={() => !isAuthenticated && trackUpgradeClick('landing_dashboard_preview_cta')}
                      className="btn btn-gold"
                    >
                      {isAuthenticated ? 'View Full Analytics →' : 'Go Premium – Upgrade Now'}
                    </Link>
                  </div>
                </>
              )}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="pricing" id="pricing">
            <h2 className="section-title landing-pricing-title">Choose Your Plan</h2>
            <div className="pricing-grid">
              <div className="price-card">
                <div className="price-header">
                  <h3>Free</h3>
                  <div className="amount">$0<span>/mo</span></div>
                </div>
                <ul className="price-features">
                  <li><strong>Data delayed by 6 hours</strong></li>
                  <li>Sentiment Analysis</li>
                  <li>12 hours of historical data</li>
                  <li>60-minute intervals only</li>
                  <li>Basic WSS metrics</li>
                  <li>All discussed tickers</li>
                  <li className="disabled">Subreddit breakdown</li>
                  <li className="disabled">Full WSS analysis</li>
                  <li className="disabled">Sentiment explanation</li>
                  <li className="disabled">Price correlation data</li>
                </ul>
                <Link to="/dashboard" className="btn btn-outline landing-full-width-btn">Get Started</Link>
              </div>

              <div className="price-card pro-card">
                <div className="pro-badge">{isPro ? 'Your plan' : 'Most Popular'}</div>
                <div className="price-header">
                  <h3>Premium</h3>
                  <div className="amount">$19.99<span>/mo</span></div>
                </div>
                <ul className="price-features landing-premium-features">
                  <li><strong>Everything in Free</strong></li>
                  <li><strong>Spike tab</strong></li>
                  <li><strong>Real-time data</strong> (no delay)</li>
                  <li><strong>30 days</strong>{' '}history (60x)</li>
                  <li><strong>5 intervals</strong>: 5m, 15m, 30m, 1h, 1d</li>
                  <li><strong>Full WSS Analysis</strong></li>
                  <li><strong>Subreddit breakdown</strong></li>
                  <li><strong>Sentiment explanation</strong></li>
                  <li><strong>Price correlation</strong></li>
                  <li><strong>Extended limits</strong></li>
                </ul>
                {isPro ? (
                  <Link to="/billing" className="btn btn-outline landing-full-width-btn landing-manage-subscription-btn">Manage subscription</Link>
                ) : (
                  <Link
                    to={'/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
                    className="btn btn-gold landing-full-width-btn"
                    onClick={() => trackUpgradeClick('landing_pricing_cta')}
                  >
                    Go Premium – Upgrade Now
                  </Link>
                )}
              </div>
            </div>
            
            {/* Why Choose Premium */}
            <div className="mt-10 p-6 sm:p-8 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent">
              <div className="text-center mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-brand-gold mb-2">
                  Why Choose Premium?
                </h3>
                <p className="text-sm text-white/60 max-w-md mx-auto">
                  Premium gives you real-time data with zero delay, deeper history, and full analytics.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="flex flex-col items-center text-center p-5 rounded-xl border border-white/10 bg-black/20 hover:border-brand-gold/30 hover:bg-white/[0.04] transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-emerald-500/15 border border-emerald-500/30">
                    <Clock className="w-6 h-6 text-emerald-400" />
                  </div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-emerald-400 mb-1">0h</span>
                  <span className="text-sm font-semibold text-white/90">Data delay</span>
                  <span className="text-xs text-white/55 mt-1">Premium real-time vs Free 6h delay</span>
                </div>
                <div className="flex flex-col items-center text-center p-5 rounded-xl border border-white/10 bg-black/20 hover:border-brand-gold/30 hover:bg-white/[0.04] transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-amber-500/15 border border-amber-500/30">
                    <BarChart3 className="w-6 h-6 text-amber-400" />
                  </div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-amber-400 mb-1">5 intervals</span>
                  <span className="text-sm font-semibold text-white/90">5m, 15m, 30m, 1h, 1d</span>
                  <span className="text-xs text-white/55 mt-1">vs 60 min only on Free</span>
                </div>
                <div className="flex flex-col items-center text-center p-5 rounded-xl border border-white/10 bg-black/20 hover:border-brand-gold/30 hover:bg-white/[0.04] transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-orange-500/15 border border-orange-500/30">
                    <Flame className="w-6 h-6 text-orange-400" />
                  </div>
                  <span className="text-2xl sm:text-3xl font-extrabold text-orange-400 mb-1">Full WSS</span>
                  <span className="text-sm font-semibold text-white/90">Spike detection & breakdown</span>
                  <span className="text-xs text-white/55 mt-1">Real-time, no delay</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Landing; 
